import {useState, useRef} from 'react';
import {AgGridReact} from 'ag-grid-react';
import {formatDate, dateSorter, createYearsArr} from '../../utils/date-utils';
import {decodeAbbreviation, getRegionName} from '../../utils/abbreviation-utils';
import {renderLinkedCellData} from '../../utils/render-link-utils';
import {grid_accessibility_options} from '../../utils/grid-utils';
import {csv_headers} from '../../utils/csv-utils';
import {renderCaseStatus} from '../../utils/case-detail-renderers';
import {defaultSort} from '../../utils/utils';
import {region_arr, status_arr} from '../../utils/text-utils';
import customMatcherFilter from '../../utils/custom-filters/custom-matcher-filter';
import customYearFilter from '../../utils/custom-filters/custom-year-filter';
import customDropdownFloatingFilter from '../../utils/custom-filters/custom-dropdown-floating-filter';
import customTextFilter from '../../utils/custom-filters/custom-text-filter';
import textFloatingFilter from '../../utils/custom-filters/custom-text-floating-filter';
import ExportCSV from '../export-csv';
import FilterCaption from '../filter-caption';

export const default_column_defs = [
  {
    field: 'cpfNum',
    cellRenderer: renderLinkedCellData,
    cellRendererParams: (params) => {
      return {
        path: `/case/${params.value}`,
      };
    },
    headerName: 'Case Number',
    minWidth: 195,
    filter: customTextFilter,
    suppressMenu: true,
    floatingFilter: true,
    floatingFilterComponent: textFloatingFilter,
  },
  {
    field: 'openedDt',
    headerName: 'Date Opened',
    width: 140,
    flex: 0,
    valueGetter: (params) => {
      return formatDate(params.data.openedDt);
    },
    comparator: dateSorter,
    filter: customYearFilter,
    suppressMenu: true,
    floatingFilter: true,
    floatingFilterComponent: customDropdownFloatingFilter,
    floatingFilterComponentParams: {
      filter_id: 'opened',
      suppressFilterButton: true,
      options: createYearsArr(),
      initial_value: 'All Years',
    },
  },
  {
    field: 'operatorName',
    headerName: 'Operator',
    minWidth: 175,
    resizable: true,
    cellRenderer: renderLinkedCellData,
    cellRendererParams: (params) => {
      return {
        path: `/operator/${params.data.operatorId}`,
        value: params.data.operatorId,
        displayName: params.data.operatorName,
      };
    },
    filter: customTextFilter,
    suppressMenu: true,
    floatingFilter: true,
    floatingFilterComponent: textFloatingFilter,
  },
  {
    field: 'region',
    headerName: 'Region',
    width: 130,
    flex: 0,
    sortable: true,
    cellRenderer: getRegionName,
    filter: customMatcherFilter,
    suppressMenu: true,
    floatingFilter: true,
    floatingFilterComponent: customDropdownFloatingFilter,
    floatingFilterComponentParams: {
      filter_id: 'region',
      suppressFilterButton: true,
      options: region_arr,
      initial_value: 'All Regions',
    },
  },
  {
    field: 'closedDt',
    headerName: 'Date Closed',
    width: 120,
    flex: 0,
    comparator: dateSorter,
    valueGetter: (params) => {
      return formatDate(params.data.closedDt);
    },
    filter: customYearFilter,
    suppressMenu: true,
    floatingFilter: true,
    floatingFilterComponent: customDropdownFloatingFilter,
    floatingFilterComponentParams: {
      filter_id: 'closed',
      suppressFilterButton: true,
      options: createYearsArr(),
      initial_value: 'All Years',
    },
  },
  {
    field: 'caseStatus',
    headerName: 'Case Status',
    width: 120,
    flex: 0,
    cellRenderer: renderCaseStatus,
    filter: customMatcherFilter,
    suppressMenu: true,
    floatingFilter: true,
    floatingFilterComponent: customDropdownFloatingFilter,
    floatingFilterComponentParams: {
      filter_id: 'status',
      suppressFilterButton: true,
      options: status_arr,
    },
  },
  {
    field: 'caseType',
    hide: true,
    headerName: 'Case Type',
    cellRenderer: (params) => decodeAbbreviation(params.value),
  },
];

const default_col_def = {sortable: true, comparator: defaultSort, resizable: true, flex: 1};

const query_dict = {
  opened_yr: 'openedDt',
  closed_yr: 'closedDt',
  action_yr: 'orderDt',
};

const CaseListGrid = ({
  data,
  col_defs = default_column_defs,
  show_csv = true,
  csv_name_root = 'cases',
  search_query: initial_search_query = {},
  grid_class_name = 'grid-col-10',
  label_string,
  label_footnote,
  aria_labelledby = 'table-label',
  csv_only_column_defs = [],
}) => {
  const gridRef = useRef();
  const cases = data;

  const [csv_data, setCsvData] = useState(data);
  const [csv_filter_note, setCsvFilterNote] = useState(false);
  const [is_manually_filtered, setIsManuallyFiltered] = useState(false);

  // add default column definitions for any csv-only fields here, if desired
  const default_csv_only_column_defs = [
    // {
    //   field: 'howdiscove',
    //   headerName: 'Discovery Method',
    // },
  ];

  // Merge the incoming csv-only column defs with the defaults, then
  // exclude any of the fields that are not found in the query's first object
  csv_only_column_defs = [...new Set([...default_csv_only_column_defs, ...csv_only_column_defs])].filter((col) => {
    return Object.keys(cases[0]).includes(col.field);
  });

  const updateQueryStrings = (filter_model) => {
    // Only update the query strings the filter has been manually applied
    // If this isn't guarded, the query strings flash before `setFilterModel` is applied in `onGridReady`
    if (is_manually_filtered) {
      // Specify the object for two reasons:
      // The order of the object seems to dictate the order of the query strings (even though it's important to note that objects don't have order)
      // By specifying keys, non-specified filters won't be concatenated as query strings (i.e. Region)
      const search_query = {
        opened_yr: '',
        closed_yr: '',
        action_yr: '',
      };
      Object.keys(search_query).map((key) => {
        const curr_filter = filter_model[query_dict[key]];
        if (curr_filter) {
          search_query[key] = curr_filter['state'];
        } else {
          delete search_query[key];
        }
      });
      // Change the URL without navigating anywhere
      // https://stackoverflow.com/questions/10970078/modifying-a-query-string-without-reloading-the-page
      if (!Object.keys(search_query).length) {
        window.history.replaceState({path: location.pathname}, '', location.pathname);
      } else {
        // compile the updated query keys and values into a query string
        const query_string = '?' + new URLSearchParams(search_query).toString();
        window.history.replaceState({path: location.pathname + query_string}, '', location.pathname + query_string);
      }
    }
  };

  const updateCsvData = () => {
    // the `data` value for each node in `rowsToDisplay`
    // contains the full set of queried attributes,
    // so it can furnish what the CSV exporter needs
    // even if the AG-Grid is only displaying a subset
    if (gridRef.current) {
      let new_csv_data = [];
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        let new_csv_row = {...node.data};
        new_csv_data.push(new_csv_row);
      });
      setCsvData(new_csv_data);
    }
  };

  const onGridReady = () => {
    const filter_model = {};
    Object.entries(initial_search_query).map(([key, value]) => {
      const col_field = query_dict[key];
      filter_model[col_field] = {state: value};
    });
    gridRef.current.api.setFilterModel(filter_model);
    // Per client's request, .nopv-grid wraps the header, thus needs bigger height
    if (col_defs.some((col) => col.field == 'proposedPenalties')) gridRef.current.api.setHeaderHeight(60);
  };

  const onFilterChanged = (grid) => {
    const curr_filters = grid.api.getFilterModel();
    updateQueryStrings(curr_filters);
    setCsvFilterNote(true);
    updateCsvData();
  };

  // TODO: make the name of the file reflect the case/order/action
  // taxonomy as well as any applied filters

  // Let AGGrid calculate table height if there aren't many rows
  let height = 'normal';
  let height_class = '';
  if (cases.length <= 25) {
    height = 'autoHeight';
  } else {
    height_class = 'set-height';
  }
  return (
    <>
      {label_string && (
        <h3 id="table-label" className="usa-table-label">
          {label_string}
          {label_footnote && (
            <sup>
              (
              <a href="#fn1" id="fn1-link">
                1
              </a>
              )
            </sup>
          )}
        </h3>
      )}
      <div className={`ag-theme-balham case-list-grid usa-prose ${height_class}`} aria-labelledby={aria_labelledby}>
        {gridRef.current && <FilterCaption filterModel={gridRef.current.api.getFilterModel()} />}
        <div className={'ag-grid-container ' + grid_class_name}>
          <AgGridReact
            ref={gridRef}
            rowData={cases}
            defaultColDef={default_col_def}
            columnDefs={col_defs}
            className="usa-table usa-table--compact"
            ensureDomOrder={grid_accessibility_options.ensureDomOrder}
            suppressColumnVirtualisation={grid_accessibility_options.suppressColumnVirtualisation}
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            onFilterOpened={() => setIsManuallyFiltered(true)}
            domLayout={height}
          />
        </div>
      </div>
      {show_csv && (
        <ExportCSV
          data={csv_data}
          exported_filename={csv_name_root}
          headers={csv_headers([...new Set([...col_defs, ...csv_only_column_defs])])}
          filter_note={csv_filter_note}
        />
      )}
    </>
  );
};

export default CaseListGrid;
